import { FC, ReactNode, useCallback, MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { driverNeeds } from 'selectors'
import styled, { css } from 'styled-components/macro'
import FlashConnector from './FlashConnector'
import ToastMessage from 'components/layout/ToastMessage'
import { Box, Button, Text, Container, Icon, Spacer, Messages, parsers } from 'roadie-ui'

import { store } from 'index'
import { ExternalLink } from 'components/layout/links'
import { LockReason } from 'types/profile'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'
import type { TranslationKey } from 'localization'
import { getClusterServiceUrl } from 'util/server/cluster'
import { clearApi, isApiCustomized } from 'util/server/server'

type StoreStateType = ReturnType<typeof store.getState>

const RoadieLogoLink = styled(Link)`
  align-items: center;
  display: inline-flex;
`

const FlashStickyWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100000000;
`

const LockBannerMessage: FC<{ reason: string; subReason: string }> = ({ reason, subReason }) => {
  const { t } = useLocalization()
  // Default message and link
  let lockBannerMessageKey: TranslationKey = 'dweb_lock_banner_account_locked'
  let lockBannerLink = `${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Driver-Gig-logistics-Unlocking-your-account`

  switch (reason) {
    case 'failed_background_check':
      lockBannerMessageKey = 'dweb_lock_banner_consumer_report_issue'
      lockBannerLink = `${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Driver-policies-Understanding-consumer-reports-and-FAQ`
      break
    case 'inactive':
      lockBannerMessageKey = 'dweb_lock_banner_inactive_account'
      break
    case 'research':
    case 'legal_hold':
      lockBannerMessageKey = 'dweb_lock_banner_account_under_review'
      break
  }

  // Subreason specific messages take precedence over the reason specific message
  switch (subReason) {
    case 'Reported Issues (XD Return Window Exceeded)':
      lockBannerMessageKey = 'dweb_lock_banner_xd_return_deadline_missed'
      lockBannerLink = `${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Returning-XD-Gigs`
      break
  }

  const lockBannerMessage = t(lockBannerMessageKey)

  return lockBannerLink ? (
    <Text.Subtitle1 id="flash-text" $overflow="hidden" $textOverflow="ellipsis">
      <LocalizedRichText
        translationKey="dweb_rui_widgets_main_layout_value1_click_link1_value2"
        params={{
          value1: lockBannerMessage,
          link1: (
            <ExternalLink href={lockBannerLink}>
              <u>
                <LocalizedText translationKey="dweb_common_here" />
              </u>
            </ExternalLink>
          ),
          value2:
            subReason == 'inactive'
              ? t('dweb_rui_widgets_main_layout_learn_more_account_inactivity')
              : t('dweb_rui_widgets_main_layout_for_more_information'),
        }}
      />
    </Text.Subtitle1>
  ) : (
    <Text.Subtitle1 id="flash-text" $overflow="hidden" $textOverflow="ellipsis">
      {lockBannerMessage}
    </Text.Subtitle1>
  )
}

const LockBanner: FC<{ lockReason: LockReason }> = (props) => {
  if (props.lockReason?.subreason === 'duplicate_account') {
    // Duplicate account is handled through a separate page, not a flash banner
    return null
  }
  if (props.lockReason?.reason === 'permanent') {
    // Permanent lock reason handled in account status page
    return null
  }

  return (
    <Container.Position
      $position={false ? 'absolute' : 'initial'}
      $maxHeight={false ? '1px' : 'initial'}
      $zIndex="1"
      $width="100%"
      $overflow="hidden"
    >
      <Container.Grid
        $gridTemplateColumns="2rem 1fr 2rem"
        $gridGap="1rem"
        $alignItems="center"
        $padding="1rem 2rem"
        $textAlign="center"
        $color="neutral-white"
        $backgroundColor="error-base"
      >
        <span />
        <LockBannerMessage reason={props.lockReason?.reason || ''} subReason={props.lockReason?.subreason || ''} />
      </Container.Grid>
    </Container.Position>
  )
}

const HeaderLink: FC<{
  children: ReactNode
  active?: boolean
  to: string
  external?: boolean
  id?: string
}> = (props) => {
  const LinkComponent = props.external ? Button.A.Primary : Button.Link.Primary

  return (
    <LinkComponent
      id={props.id}
      href={props.external ? props.to : undefined}
      to={props.to}
      $padding="2.2rem 3.5rem"
      $paddingMedia={{ mobile: '2.2rem 2rem' }}
      $display="inline-block"
      $backgroundColor={
        props.active
          ? parsers.surfaceColor.modify('neutral-black', parsers.baselineColor.modifiers.opacity(0.4))
          : 'clear'
      }
      $backgroundColorHover={
        props.active
          ? parsers.surfaceColor.modify('neutral-black', parsers.baselineColor.modifiers.opacity(0.4))
          : 'clear'
      }
      $borderColor="clear"
      $borderRadius="0"
    >
      {props.children}
    </LinkComponent>
  )
}

const Section = styled.section<{ above?: boolean }>`
  z-index: 0;
  transition-delay: 350ms;
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${(props) =>
    props.above &&
    css`
      z-index: 1;
      transition-delay: 0ms;
    `}
`

const NavLinks: FC<{ children: ReactNode }> = (props) => (
  <Container.Grid $gridTemplateColumns="auto auto">{props.children}</Container.Grid>
)

const SidebarMenuIcon: FC<{
  sidebar: any
  onClick: (event: MouseEvent) => void
}> = (props) => {
  if (props.sidebar) {
    return (
      <Container.Base $displayMedia={{ ultrawide: 'none', desktop: 'none' }} $height="3rem">
        <Icon.Menu $color="neutral-white" $width="3rem" $cursor="pointer" onClick={props.onClick} />
      </Container.Base>
    )
  } else {
    return null
  }
}

const BGWrapper = styled.div`
  > * {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
`

const CustomApi = () => {
  const unset = useCallback(() => {
    clearApi()
    const url = new URL(window.location.href)
    url.searchParams.delete('api')
    window.location.href = url.toString()
  }, [])
  if (isApiCustomized()) {
    return (
      <Box.Model
        $padding=".5rem 0 0"
        $textAlign="center"
        $backgroundColor="alert-base"
        $borderBottomWidth="3px"
        $borderBottomStyle="dashed"
        $borderBottomColor={'primary-dark'}
        $color="neutral-black"
        $height="5rem"
      >
        <Container.Grid $gridTemplateColumns="auto auto" $alignItems="center" $justifyItems="center">
          <span>
            <b>
              <LocalizedText translationKey="dweb_rui_widgets_main_layout_custom_main_api" />
            </b>{' '}
            {getClusterServiceUrl('api')}
            {'; '}
            <b>
              <LocalizedText translationKey="dweb_rui_widgets_main_layout_custom_vehicles_api" />
            </b>{' '}
            {getClusterServiceUrl('vehicles')}
            {'; '}
            <b>
              <LocalizedText translationKey="dweb_rui_widgets_main_layout_custom_driver_performance_api" />
            </b>{' '}
            {getClusterServiceUrl('driver-performance')}
            {'; '}
            <b>CUSTOM DRIVER-SCHEDULING-API:</b> {getClusterServiceUrl('driver-scheduling')}
            {'; '}
            <b>
              <LocalizedText translationKey="dweb_rui_widgets_main_layout_custom_signing_api" />
            </b>{' '}
            {getClusterServiceUrl('signing-service')}
          </span>
          <Button.Primary $backgroundColor="alert-base" onClick={unset}>
            <LocalizedText translationKey="dweb_rui_widgets_main_layout_unset" />
          </Button.Primary>
        </Container.Grid>
      </Box.Model>
    )
  }
  return null
}

const HeaderLogo = () => {
  const mediaSize = useSelector((state: StoreStateType) => state.config.mediaSize)

  const svg = mediaSize === 'mobile' ? 'Logo' : 'RoadieUpsCompany'

  return <Icon svg={svg} $color="neutral-white" $height="4rem" />
}

const AppLinks: FC<{ appRootPath: string }> = () => {
  return (
    <NavLinks>
      <HeaderLink id="send-app-link" to={`https://${import.meta.env.REACT_APP_SEND_DOMAIN}`} external>
        <LocalizedText translationKey="dweb_rui_widgets_main_layout_send" />
      </HeaderLink>
      <HeaderLink id="drive-app-link" to="/" active>
        <LocalizedText translationKey="dweb_rui_widgets_main_layout_drive" />
      </HeaderLink>
    </NavLinks>
  )
}

const UserLinks: FC = () => (
  <NavLinks>
    <HeaderLink id="header-sign-out" to="/sign-out">
      <LocalizedText translationKey="dweb_common_sign_out" />
    </HeaderLink>
  </NavLinks>
)

type LayoutProps = {
  children: ReactNode
  sidebar?: ReactNode
  background?: ReactNode
  needs?: string
  header?: false | ReactNode
  footer?: boolean
  fullscreen?: boolean
}

const MainLayout: FC<LayoutProps> = (props) => {
  const {
    children,
    sidebar,
    header = true,
    footer = true,
    background = <Container.Base $backgroundColor="neutral-white" />,
    fullscreen = false,
  } = props
  const { profile, appRootPath, sidebarOpen, toast, needs, isLocked, lockReason } = useSelector(
    (state: StoreStateType) => {
      const {
        profile,
        config,
        mfa,
        authenticityVerificationState,
        isLocked,
        lockReason,
        termsDocuments,
        disclosureDocuments,
      } = state
      const { isWebview } = config
      let needs = null
      if (profile) {
        needs = driverNeeds(
          mfa,
          profile,
          lockReason,
          isWebview,
          authenticityVerificationState,
          termsDocuments,
          disclosureDocuments
        )
      }
      return {
        profile: profile,
        appRootPath: config.appRootPath,
        sidebarOpen: config.sidebarOpen,
        toast: config.toast,
        needs,
        isLocked,
        lockReason,
      }
    }
  )
  const dispatch = useDispatch()
  const handleOpenSidebar = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      dispatch({ type: 'SET_SIDEBAR_OPEN', open: true })
    },
    [dispatch]
  )
  let maxWidth = '102.5rem'
  let pagePadding = '5rem'

  if (fullscreen) {
    maxWidth = '100%'
    pagePadding = '0'
  }
  const showingSidebar = !!((!needs || needs === 'restricted-access') && !!sidebar)

  return (
    <div>
      <BGWrapper>{background}</BGWrapper>
      <Container.Grid $minHeight="100vh" $gridTemplateRows="auto 1fr auto">
        <div>
          <CustomApi />
          {header === true ? (
            <Container.Grid
              $backgroundColor="primary-darker"
              $alignItems="center"
              $justifyContent="stretch"
              $justifyItems="center"
            >
              <Container.Grid
                $height="6.4rem"
                $width="100%"
                $maxWidth={maxWidth}
                $justifyContent="space-between"
                $gridTemplateColumns="auto auto"
                $alignItems="center"
                $padding="0 1.5rem"
                $paddingMedia={{ mobile: '0' }}
              >
                <Container.Grid
                  $alignItems="center"
                  $padding="0 3.5rem"
                  $paddingMedia={{ mobile: '0 2rem' }}
                  $gridTemplateColumnsMedia={{ ultrawide: 'auto', desktop: 'auto' }}
                  $gridGapMedia={{ ultrawide: '0', desktop: '0' }}
                  $gridTemplateColumns="3rem auto"
                  $gridGap="1.5rem"
                >
                  <SidebarMenuIcon onClick={handleOpenSidebar} sidebar={sidebar} />
                  <RoadieLogoLink id="logo" to={appRootPath || '/'}>
                    <HeaderLogo />
                  </RoadieLogoLink>
                </Container.Grid>
                <Container.Grid $gridTemplateColumns="auto auto auto">
                  {profile ? <UserLinks /> : <AppLinks appRootPath={appRootPath} />}
                </Container.Grid>
              </Container.Grid>
              {isLocked && <LockBanner lockReason={lockReason} />}
            </Container.Grid>
          ) : header ? (
            header
          ) : null}
        </div>
        <div>
          <FlashStickyWrapper>
            <FlashConnector />
          </FlashStickyWrapper>

          <ToastMessage toast={toast} />

          <Container.Grid>
            <Container.GridItemGrid
              $padding={`${pagePadding} ${pagePadding} 0`}
              $paddingMedia={{ mobile: '0' }}
              $width="100%"
              $maxWidth={maxWidth}
              $gridTemplateColumns={showingSidebar ? 'auto 1fr' : '100%'}
              $justifySelf="center"
            >
              <Section above={sidebarOpen}>{showingSidebar ? sidebar : null}</Section>
              <Section>{children}</Section>
            </Container.GridItemGrid>
          </Container.Grid>
        </div>
        <div>
          {footer ? (
            <div>
              <Spacer $height="4rem" />
              <Container.Position $position="relative">
                <Container.Base>
                  <Container.Base $textAlign="center">
                    <Text.Body2 baseElement="span">
                      <a href="https://support.roadie.com" target="_blank" rel="noopener nofollower noreferrer">
                        <LocalizedText translationKey="dweb_rui_widgets_main_layout_help" />
                      </a>
                    </Text.Body2>
                    <Text.Body2 baseElement="span" $userSelect="none" $color="neutral-light">
                      {' // '}
                    </Text.Body2>
                    <Text.Body2 baseElement="span">
                      <a href="https://www.roadie.com/privacy" target="_blank" rel="noopener nofollower noreferrer">
                        <LocalizedText translationKey="dweb_rui_widgets_main_layout_privacy" />
                      </a>
                    </Text.Body2>
                    <Text.Body2 baseElement="span" $userSelect="none" $color="neutral-light">
                      {' // '}
                    </Text.Body2>
                    <Text.Body2 baseElement="span">
                      <a href="https://www.roadie.com/terms" target="_blank" rel="noopener nofollower noreferrer">
                        <LocalizedText translationKey="dweb_rui_widgets_main_layout_terms" />
                      </a>
                    </Text.Body2>
                  </Container.Base>
                  <Spacer $height=".4rem" />
                  <Text.Body2 $textAlign="center">
                    <LocalizedRichText
                      translationKey="dweb_rui_widgets_main_layout_value1_roadie_inc_all_rights_reserved"
                      params={{ value1: new Date().getFullYear() }}
                    />
                  </Text.Body2>
                </Container.Base>
              </Container.Position>
              <Spacer $height="4rem" />
            </div>
          ) : null}
        </div>
      </Container.Grid>
      <Messages app="drive" />
    </div>
  )
}

export default MainLayout
